import { notifyManager } from '@tanstack/react-query';

type ReassertEdge = { targetSiblingId: number; position: 'above' | 'below'; token: number };

/** The (before, after) pair this edge implies — e.g. "A after B" implies B comes before A. */
const orderedPair = (movedId: number, edge: ReassertEdge): readonly [number, number] =>
  edge.position === 'below' ? [edge.targetSiblingId, movedId] : [movedId, edge.targetSiblingId];

/**
 * True if adding this before/after constraint would create a cycle in the order implied by every
 * other active edge. Mixed positions can describe a consistent order ("A after B", "B before C",
 * "C after A" is the satisfiable B < A < C), so this checks the ordering, not just id reuse.
 */
const wouldCreateCycle = (
  edges: ReadonlyMap<number, ReassertEdge>,
  from: number,
  to: number,
  position: 'above' | 'below',
): boolean => {
  const [before, after] = position === 'below' ? [to, from] : [from, to];
  const visited = new Set<number>();
  const queue = [after];
  while (queue.length > 0) {
    const current = queue.shift() as number;
    if (current === before) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    for (const [movedId, edge] of edges) {
      const [b, a] = orderedPair(movedId, edge);
      if (b === current) queue.push(a);
    }
  }
  return false;
};

export type ReassertCycleGuard = {
  /** Runs `write` only if it wouldn't close a cycle with another active edge; returns whether it ran. */
  attempt: (
    movedId: number,
    targetSiblingId: number,
    position: 'above' | 'below',
    write: () => void,
  ) => boolean;
  /** Drops `movedId`'s edge outright — e.g. when a fresh drag supersedes whatever it was doing. */
  clear: (movedId: number) => void;
};

/**
 * Two rows anchored on each other have mutually falsifying predicates; a nested reassert backs
 * off instead of looping. Blocked only when a row's write would close a cycle in the order
 * implied by every other active edge, not client-wide, so an unrelated row is never suppressed.
 * Each edge clears independently once its own write settles.
 */
export const createReassertCycleGuard = (): ReassertCycleGuard => {
  const edges = new Map<number, ReassertEdge>();
  // Monotonic and never reused, so a deleted-then-recreated edge can't collide with a
  // still-scheduled older cleanup's token.
  let nextToken = 0;

  const attempt: ReassertCycleGuard['attempt'] = (movedId, targetSiblingId, position, write) => {
    if (wouldCreateCycle(edges, movedId, targetSiblingId, position)) return false;

    const token = ++nextToken;
    edges.set(movedId, { targetSiblingId, position, token });
    // Drops this row's own edge only if a newer one for the same row hasn't superseded it.
    const clearIfStale = () => {
      if (edges.get(movedId)?.token === token) edges.delete(movedId);
    };
    try {
      write();
    } catch (error) {
      // Never wrote, so it never occupied an edge.
      clearIfStale();
      throw error;
    }
    // Rides whatever scheduler TanStack itself uses to flush this write's own notifications.
    notifyManager.schedule(clearIfStale);
    return true;
  };

  return { attempt, clear: (movedId) => edges.delete(movedId) };
};
